/**
 * Helpers component - Horizontal flex with helper icons or text and dropdown chevron on the right
 * Factory function following the app's architectural patterns
 */

import { createIconButton, type IconButtonAPI } from "@UI/IconButton/index.js";
import { createChevron, type ChevronAPI } from "@UI/Chevron/index.js";
import {
  createHelpersModal,
  type HelpersModalAPI,
  type HelpersModalConfig,
} from "./subcomponents/HelpersModal/index.js";
import { logger } from "@/utils/logger.ts";
import "./Helpers.scss";

export type HelperType =
  | "rulers"
  | "snap"
  | "guides"
  | "dots"
  | "lines"
  | "overlay";

export interface HelpersConfig {
  readonly onDropdownClick?: () => void;
  readonly onHelperToggle?: (helper: HelperType, active: boolean) => void;
  readonly onChange?: (helpers: readonly HelperType[]) => void;
  readonly initialHelpers?: readonly HelperType[];
}

export interface HelpersAPI {
  readonly getElement: () => HTMLElement;
  readonly setActiveHelpers: (helpers: readonly HelperType[]) => void;
  readonly setAllowedHelpers: (
    helpers: readonly HelperType[] | null | undefined
  ) => void;
  readonly getActiveHelpers: () => readonly HelperType[];
  readonly toggleHelper: (helper: HelperType) => void;
  readonly destroy: () => void;
}

/**
 * Creates Helpers component with helper icons or text and dropdown chevron
 * @param config - Configuration for the helpers
 * @returns Helpers API
 */
export const createHelpers = (config: HelpersConfig = {}): HelpersAPI => {
  const {
    onDropdownClick,
    onHelperToggle,
    onChange,
    initialHelpers = [],
  } = config;

  let isDestroyed = false;
  let activeHelpers: HelperType[] = [...initialHelpers];
  let helperIconAPIs: Map<HelperType, IconButtonAPI> = new Map();
  let helpersTextElement: HTMLElement | null = null;
  let helpersModalAPI: HelpersModalAPI | null = null;
  let dropdownChevronElement: HTMLElement | null = null;
  let allowedHelpers: Set<HelperType> | null = null;

  // Create container element
  const container = document.createElement("div");
  container.className = "helpers-controls";

  /**
   * Helper type to icon mapping
   */
  const getHelperIcon = (helper: HelperType): string => {
    switch (helper) {
      case "rulers":
        return "helpers_rulers";
      case "snap":
        return "helpers_snap";
      case "guides":
        return "helpers_guides";
      case "dots":
        return "helpers_dots";
      case "lines":
        return "helpers_lines";
      case "overlay":
        return "helpers_overlay";
      default:
        return "helpers_rulers"; // Fallback to rulers icon
    }
  };

  /**
   * Create the helpers text element (when no helpers are active)
   */
  const createHelpersText = (): HTMLElement => {
    const textElement = document.createElement("span");
    textElement.className = "helpers-text";
    textElement.textContent = "HELPERS";
    return textElement;
  };

  /**
   * Create a helper icon button
   */
  const createHelperIcon = (helper: HelperType): IconButtonAPI => {
    const iconAPI = createIconButton({
      icon: getHelperIcon(helper),
      size: 24,
      iconSize: 24,
      behavior: "button",
      className: `helper-icon helper-icon--${helper}`,
      onClick: () => {
        if (onHelperToggle) {
          const isActive = activeHelpers.includes(helper);
          onHelperToggle(helper, !isActive);
        }
      },
    });

    return iconAPI;
  };

  /**
   * Create the dropdown chevron element (aligned to the right)
   */
  const handleDropdownClick = (): void => {
    if (helpersModalAPI) {
      helpersModalAPI.destroy();
      helpersModalAPI = null;
      return;
    }
    if (!dropdownChevronElement) return;
    const modalConfig: HelpersModalConfig = {
      anchorElement: dropdownChevronElement,
      selected: activeHelpers,
      ...(allowedHelpers && allowedHelpers.size
        ? {
            allowed: Array.from(allowedHelpers) as readonly HelperType[],
          }
        : {}),
      onChange: (next: readonly HelperType[]) => {
        activeHelpers = [...next];
        updateDisplay();
        // Prefer single-shot onChange if provided
        if (onChange) {
          onChange(activeHelpers);
        } else {
          // Backward-compatible: bubble per-item toggles
          next.forEach((h) => onHelperToggle?.(h, true));
          (
            ["rulers", "snap", "guides", "dots", "lines", "overlay"] as const
          ).forEach((h) => {
            if (!next.includes(h)) onHelperToggle?.(h, false);
          });
        }
      },
      onClose: () => {
        helpersModalAPI = null;
      },
    };
    helpersModalAPI = createHelpersModal(modalConfig);
    helpersModalAPI.show();
  };

  const createDropdownChevron = (): HTMLElement => {
    const api: ChevronAPI = createChevron({
      orientation: "down",
      className: "dropdown-container",
      svgClassName: "dropdown-chevron",
      onClick: onDropdownClick ?? handleDropdownClick,
    });
    return api.getElement();
  };

  /**
   * Update the display based on active helpers
   */
  const updateDisplay = (): void => {
    // Clear existing content
    container.innerHTML = "";

    // Create content container for helpers
    const contentContainer = document.createElement("div");
    contentContainer.className = "helpers-content";

    const helpersToRender = allowedHelpers
      ? activeHelpers.filter((helper) => allowedHelpers?.has(helper))
      : [...activeHelpers];

    logger.trace("helpers", "🖼️ Helpers component: updateDisplay", {
      count: helpersToRender.length,
    });

    if (helpersToRender.length === 0) {
      // Show "HELPERS" text when no helpers are active
      logger.trace("helpers", "🖼️ Displaying HELPERS text");
      helpersTextElement = createHelpersText();
      contentContainer.appendChild(helpersTextElement);
    } else {
      // Show helper icons
      logger.trace("helpers", "🖼️ Displaying helper icons", {
        count: helpersToRender.length,
      });
      helpersTextElement = null;
      helperIconAPIs.clear();

      helpersToRender.forEach((helper) => {
        const iconAPI = createHelperIcon(helper);
        helperIconAPIs.set(helper, iconAPI);
        contentContainer.appendChild(iconAPI.getElement());
      });
    }

    // Add content container to main container
    container.appendChild(contentContainer);

    // Add dropdown chevron (always on the right)
    dropdownChevronElement = createDropdownChevron();
    container.appendChild(dropdownChevronElement);
  };

  /**
   * Set the active helpers and update display
   */
  const setActiveHelpers = (helpers: readonly HelperType[]): void => {
    if (isDestroyed) return;

    activeHelpers = allowedHelpers
      ? helpers.filter((helper) => allowedHelpers?.has(helper))
      : [...helpers];
    updateDisplay();
  };

  /**
   * Get the current active helpers
   */
  const getActiveHelpers = (): readonly HelperType[] => {
    return [...activeHelpers];
  };

  const setAllowedHelpers = (
    helpers: readonly HelperType[] | null | undefined
  ): void => {
    if (isDestroyed) return;
    allowedHelpers = helpers && helpers.length ? new Set(helpers) : null;
    activeHelpers = allowedHelpers
      ? activeHelpers.filter((helper) => allowedHelpers?.has(helper))
      : activeHelpers;
    updateDisplay();
  };

  /**
   * Toggle a specific helper
   */
  const toggleHelper = (helper: HelperType): void => {
    if (isDestroyed) return;

    if (allowedHelpers && !allowedHelpers.has(helper)) return;

    const isActive = activeHelpers.includes(helper);
    if (isActive) {
      activeHelpers = activeHelpers.filter((h) => h !== helper);
    } else {
      activeHelpers = [...activeHelpers, helper];
    }

    updateDisplay();

    // Notify callback
    if (onHelperToggle) {
      onHelperToggle(helper, !isActive);
    }
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Destroy all helper icon APIs
    helperIconAPIs.forEach((iconAPI) => {
      iconAPI.destroy();
    });
    helperIconAPIs.clear();

    // Clear references
    helpersTextElement = null;

    // Remove from DOM if still connected
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }

    // Destroy modal if open
    helpersModalAPI?.destroy();
    helpersModalAPI = null;
    dropdownChevronElement = null;
  };

  // Initialize the component
  updateDisplay();

  return {
    getElement: () => container,
    setActiveHelpers,
    setAllowedHelpers,
    getActiveHelpers,
    toggleHelper,
    destroy,
  };
};

/**
 * Creates Helpers component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the helpers
 * @returns Helpers API
 */
export const createHelpersInContainer = (
  container: HTMLElement,
  config: HelpersConfig = {}
): HelpersAPI => {
  const helpers = createHelpers(config);
  container.appendChild(helpers.getElement());
  return helpers;
};
